import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '../../../auth/useAuth0';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faPlus, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import { useBusinessContext } from '../../../context/BusinessContext';
import {
    aiChat,
    aiConversations,
    aiMessages,
    aiPendingProposals,
    AiConversationSummary,
    AiProposal,
} from '../../../service/ai/ai-service';
import AiMessageContent from './AiMessageContent';
import AiProposalCard from './AiProposalCard';
import './AiHomeComponent.css';

interface ChatMessage {
    role: 'user' | 'assistant';
    content: string;
    error?: boolean;
    /** Action proposals of this turn — cards rendered from server data. */
    proposals?: AiProposal[];
}

/**
 * AI assistant home screen: a welcoming hero (big prompt + suggestion chips) that
 * turns into a chat once a question is asked. Past conversations live in the
 * "Recentes" rail on the left — loaded per user + selected business, so switching
 * business switches history too.
 */
const AiHomeComponent: React.FC = () => {
    const { t } = useTranslation();
    const { getAccessTokenSilently } = useAuth0();
    const { selectedBusiness } = useBusinessContext();
    const businessId = selectedBusiness ? Number(selectedBusiness.id) : null;

    const [conversations, setConversations] = useState<AiConversationSummary[]>([]);
    const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [input, setInput] = useState('');
    const [busy, setBusy] = useState(false);
    const [loadingConversation, setLoadingConversation] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);

    const inChat = activeConversationId !== null || messages.length > 0 || busy;

    const suggestions: string[] = t('ai.home.suggestions', { returnObjects: true }) as unknown as string[];

    const refreshConversations = useCallback(async () => {
        if (!businessId) return;
        try {
            const token = await getAccessTokenSilently();
            setConversations(await aiConversations(businessId, token));
        } catch {
            // The rail is a convenience — the chat still works without it.
        }
    }, [businessId, getAccessTokenSilently]);

    // Business switch resets the screen to that business's own history.
    useEffect(() => {
        setConversations([]);
        setActiveConversationId(null);
        setMessages([]);
        void refreshConversations();
    }, [businessId, refreshConversations]);

    // Long replies: land at the TOP of the assistant's answer so the user reads
    // from the beginning (scrolling to the bottom forced them to scroll back up).
    // While typing/sending, keep following the bottom.
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const last = messages[messages.length - 1];
        if (!busy && last && last.role === 'assistant') {
            const nodes = el.querySelectorAll<HTMLElement>('.ai-msg');
            const target = nodes[nodes.length - 1];
            if (target) {
                const top = target.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop;
                el.scrollTo({ top: Math.max(0, top - 8), behavior: 'smooth' });
                return;
            }
        }
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }, [messages, busy]);

    const openConversation = async (id: string) => {
        if (!businessId || busy) return;
        setLoadingConversation(true);
        setActiveConversationId(id);
        try {
            const token = await getAccessTokenSilently();
            const history = await aiMessages(businessId, id, token);
            const msgs: ChatMessage[] = history.map((m) => ({
                role: m.role === 'ASSISTANT' ? 'assistant' : 'user',
                content: m.content,
            }));
            // Rehydrate still-pending confirmation cards (expired ones never come
            // back — the backend filters them, no expiry job needed). Attached to
            // the last assistant message so they render where the user expects.
            try {
                const pending = await aiPendingProposals(businessId, id, token);
                if (pending.length > 0) {
                    const lastAssistant = msgs.map((m) => m.role).lastIndexOf('assistant');
                    if (lastAssistant >= 0) {
                        msgs[lastAssistant] = { ...msgs[lastAssistant], proposals: pending };
                    } else {
                        msgs.push({ role: 'assistant', content: '', proposals: pending });
                    }
                }
            } catch {
                // Cards are a bonus on reload — the conversation still works.
            }
            setMessages(msgs);
        } catch {
            setMessages([{ role: 'assistant', content: t('ai.errorGeneric'), error: true }]);
        } finally {
            setLoadingConversation(false);
        }
    };

    const newConversation = () => {
        setActiveConversationId(null);
        setMessages([]);
        setInput('');
    };

    const send = async (text: string) => {
        const question = text.trim();
        if (!question || busy || !businessId) return;
        setMessages((prev) => [...prev, { role: 'user', content: question }]);
        setInput('');
        setBusy(true);
        try {
            const token = await getAccessTokenSilently();
            const result = await aiChat(businessId, question, activeConversationId, token);
            setActiveConversationId(result.conversationId);
            setMessages((prev) => [...prev,
                { role: 'assistant', content: result.reply, proposals: result.proposals }]);
            void refreshConversations();
        } catch (e) {
            const raw = e instanceof Error ? e.message : '';
            const friendly = raw && !raw.startsWith('ai-service') && !raw.includes('Unauthorized')
                ? raw : t('ai.errorGeneric');
            setMessages((prev) => [...prev, { role: 'assistant', content: friendly, error: true }]);
        } finally {
            setBusy(false);
        }
    };

    if (!businessId) return null;

    return (
        <div className="ai-home" data-cy="ai-home">
            {/* ---------- Recentes rail ---------- */}
            <aside className="ai-home__rail">
                <button type="button" className="ai-home__new" onClick={newConversation} data-cy="ai-home-new">
                    <FontAwesomeIcon icon={faPlus} /> {t('ai.home.newChat')}
                </button>
                <div className="ai-home__rail-title">{t('ai.home.recent')}</div>
                <div className="ai-home__rail-list">
                    {conversations.length === 0 && (
                        <div className="ai-home__rail-empty">{t('ai.home.noHistory')}</div>
                    )}
                    {conversations.map((c) => (
                        <button
                            key={c.id}
                            type="button"
                            className={`ai-home__rail-item${c.id === activeConversationId ? ' is-active' : ''}`}
                            onClick={() => void openConversation(c.id)}
                            title={c.title ?? ''}
                            data-cy={`ai-home-conv-${c.id}`}
                        >
                            {c.title || t('ai.home.untitled')}
                        </button>
                    ))}
                </div>
            </aside>

            {/* ---------- Main: hero OR chat ---------- */}
            <section className="ai-home__main">
                {!inChat ? (
                    <div className="ai-home__hero">
                        <h1 className="ai-home__title">{t('ai.home.title')}</h1>
                        <p className="ai-home__subtitle">{t('ai.home.subtitle')}</p>
                        <form
                            className="ai-home__prompt"
                            onSubmit={(e) => { e.preventDefault(); void send(input); }}
                            data-cy="ai-home-prompt"
                        >
                            <textarea
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        void send(input);
                                    }
                                }}
                                placeholder={t('ai.home.placeholder')}
                                rows={3}
                                data-cy="ai-home-input"
                            />
                            <button type="submit" className="ai-home__ask" disabled={!input.trim()} data-cy="ai-home-ask">
                                <FontAwesomeIcon icon={faWandMagicSparkles} /> {t('ai.home.ask')}
                            </button>
                        </form>
                        <div className="ai-home__chips">
                            {Array.isArray(suggestions) && suggestions.map((s) => (
                                <button key={s} type="button" className="ai-home__chip"
                                        onClick={() => void send(s)} data-cy="ai-home-chip">
                                    {s}
                                </button>
                            ))}
                        </div>
                        <p className="ai-home__disclaimer">{t('ai.home.disclaimer')}</p>
                    </div>
                ) : (
                    <div className="ai-home__chat">
                        <div className="ai-home__msgs" ref={scrollRef}>
                            {loadingConversation && <div className="ai-home__loading">{t('ai.thinking')}</div>}
                            {messages.map((m, i) => (
                                <div key={i}
                                     className={`ai-msg ai-msg--${m.role}${m.error ? ' ai-msg--error' : ''}`}
                                     data-cy={`ai-home-msg-${m.role}`}>
                                    {m.role === 'assistant' && !m.error
                                        ? <AiMessageContent content={m.content} />
                                        : m.content}
                                    {m.proposals?.map((p) => (
                                        <AiProposalCard key={p.id} businessId={businessId} proposal={p} />
                                    ))}
                                </div>
                            ))}
                            {busy && (
                                <div className="ai-msg ai-msg--assistant ai-msg--typing" aria-label={t('ai.thinking')}>
                                    <span /><span /><span />
                                </div>
                            )}
                        </div>
                        <form
                            className="ai-home__inputrow"
                            onSubmit={(e) => { e.preventDefault(); void send(input); }}
                        >
                            <input
                                type="text"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder={t('ai.inputPlaceholder')}
                                disabled={busy}
                                autoFocus
                                data-cy="ai-home-chat-input"
                            />
                            <button type="submit" disabled={busy || !input.trim()}
                                    aria-label={t('ai.send')} data-cy="ai-home-chat-send">
                                <FontAwesomeIcon icon={faPaperPlane} />
                            </button>
                        </form>
                    </div>
                )}
            </section>
        </div>
    );
};

export default AiHomeComponent;
